import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformPanel } from '../../../components/AgentPlatform/AgentPlatformPanel';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { JsonTextArea } from '../../../components/AgentPlatform/JsonTextArea';
import { Button } from '../../../components/UI/Button';
import { useToast } from '../../../components/UI/ToastProvider';
import { AGENT_RUNTIME_IDS } from '../../../constants/agentPlatform';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type { AgentKnowledgeBaseDto, AgentResourceVisibility } from '../../../types/agentPlatform';
import { apTheme } from '../../../utils/agentPlatformTheme';

const emptyCreate = () => ({
  code: '',
  name: '',
  description: '',
  visibility: 'Configurable' as AgentResourceVisibility,
  configuration: '{\n  "scopeMode": "allowlist",\n  "documentIds": [],\n  "topK": 5,\n  "similarityThreshold": 0.25\n}',
});

export const AgentKnowledgeBaseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<AgentKnowledgeBaseDto | null>(null);
  const [form, setForm] = useState(emptyCreate());

  useEffect(() => {
    if (isNew || !id) return;
    agentPlatformService
      .getKnowledgeBase(id)
      .then(kb => {
        setExisting(kb);
        setForm({
          code: kb.code,
          name: kb.name,
          description: kb.description ?? '',
          visibility: kb.visibility,
          configuration: kb.configuration ?? '{}',
        });
      })
      .catch(() => addToast('Base não encontrada.', 'error'))
      .finally(() => setLoading(false));
  }, [id, isNew, addToast]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await agentPlatformService.createKnowledgeBase({
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          agentRuntimeId: AGENT_RUNTIME_IDS.pgVectorKnowledge,
          visibility: form.visibility,
          configuration: form.configuration,
        });
        addToast('Base criada.', 'success');
      } else if (id) {
        await agentPlatformService.updateKnowledgeBase(id, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          visibility: form.visibility,
          configuration: form.configuration,
        });
        addToast('Base atualizada.', 'success');
      }
      navigate('/agent-platform/knowledge-bases');
    } catch {
      addToast('Erro ao salvar base.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-500">Carregando…</div>;
  }

  return (
    <AgentPlatformFeatureGate feature="agents">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
          <AgentPlatformPageHeader
            backTo="/agent-platform/knowledge-bases"
            backLabel="Bases de conhecimento"
            title={isNew ? 'Nova base' : `Editar: ${existing?.name ?? form.name}`}
            subtitle={!isNew ? form.code : undefined}
            area="knowledge"
          />

          <AgentPlatformPanel className="max-w-2xl">
            <form onSubmit={save} className="space-y-4">
              {isNew && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Código</label>
                  <input
                    className={`${apTheme.input} font-mono`}
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
                <input
                  className={apTheme.input}
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
                <textarea
                  className={apTheme.input}
                  rows={2}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Visibilidade</label>
                <select
                  className={apTheme.input}
                  value={form.visibility}
                  onChange={e =>
                    setForm(f => ({ ...f, visibility: e.target.value as AgentResourceVisibility }))
                  }
                >
                  <option value="Configurable">Configurable</option>
                  <option value="Internal">Internal</option>
                  <option value="AgentTool">AgentTool</option>
                </select>
              </div>

              <JsonTextArea
                label="Escopo documental (scopeMode, documentIds, topK…)"
                value={form.configuration}
                onChange={v => setForm(f => ({ ...f, configuration: v }))}
                rows={8}
              />
              <p className="text-xs text-slate-500">
                Os arquivos permanecem na árvore de documentos do Know. Informe os IDs de pastas ou documentos em{' '}
                <code className="font-mono">documentIds</code> ou use <code className="font-mono">scopeMode: fullTenant</code>.
              </p>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Salvando…' : 'Salvar'}
                </Button>
                <Link to="/agent-platform/knowledge-bases">
                  <Button type="button" variant="secondary">
                    Cancelar
                  </Button>
                </Link>
              </div>
            </form>
          </AgentPlatformPanel>
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
